import React from "react";
import {InfoCircleOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";
import {Tooltip, Tag} from "antd";
import {useStore} from "../../../hooks/useStore";
import {useShiftScoreActions} from "./useShiftScoreActions";
import {LateStartIcon, EarlyEndIcon, EndTimeSpan} from "./ShiftScoreTableStyled";
import {minuteToString} from "../../../utils/helpers";
import {CourierLink} from "../../_defaultComponents/CourierLink/CourierLink";
import {TableWrapper} from "../../_defaultComponents/TableWrapper/TableWrapper";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";

const ShiftScoreTable = ({
    data,
    setData,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    setReloadCount,
    dataCount,
    isPastShifts,
    hideVerifiedPackageCount = false,
    hideActionsColumn = false,
    hideStatusColumn = false
}) => {
    const {t} = useTranslation(["shiftScore", "shiftReportStatuses", "courierStatuses"]);
    const stores = useStore();
    const {renderActionButtons, actionModals, isDedicatedIntegration} = useShiftScoreActions({
        setReloadData,
        setReloadCount,
        onApproved: reportId =>
            setData({
                rows: data.map(d => {
                    if (d?._id?.toString() === reportId.toString()) {
                        d.status = "approved";
                    }
                    return d;
                }),
                dataCount
            })
    });
    /*    useEffect(() => {
        const collection = document.getElementsByClassName("lastShift");
        const lastShift = Array.from(collection)[0];
        if (lastShift && !filters?.currentStatus==="all") {
            lastShift.innerHTML =
                '<tr class="ant-table-row ant-table-row-level-0 gray">\
                <td colspan="11" class="ant-table-cell" style="text-align:center; padding-right: 5em">\
                ⬆️ Aktif Vardiyalar ⬆️ \
                </td></tr>';
        }
    }, [data]); */

    const renderStatus = (item, status) => {
        const ACTIVE_COLOR = "processing";
        const INACTIVE_COLOR = "purple";
        const APPROVED_COLOR = "green";
        const IN_REVIEW_COLOR = "red";
        const WAITING_APPROVE_COLOR = "blue";
        const NEED_FILL_PACKAGE = "";

        let tag = null;
        if (item.isActiveShift && item.startTime) {
            tag = (
                <Tag color={ACTIVE_COLOR}>
                    {t("shiftScore.shiftStatuses.activeShift", {ns: "shiftScore"})}
                </Tag>
            );
        } else if (item.isActiveShift) {
            tag = (
                <Tag color={INACTIVE_COLOR}>
                    {t("shiftScore.shiftStatuses.noEntry", {ns: "shiftScore"})}
                </Tag>
            );
        } else {
            switch (status) {
                case "approved":
                    tag = (
                        <Tag color={APPROVED_COLOR}>
                            {t("shiftScore.shiftStatuses.approved", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                case "inReview":
                    tag = (
                        <Tag color={IN_REVIEW_COLOR}>
                            {t("shiftScore.shiftStatuses.objectionProcess", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                case "waitingApprove":
                    tag = (
                        <Tag color={WAITING_APPROVE_COLOR}>
                            {t("shiftScore.shiftStatuses.waitingApprove", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                case "needFillPackage":
                    tag = (
                        <Tag color={NEED_FILL_PACKAGE} status="processing">
                            {t("shiftScore.shiftStatuses.needFillPackage", {ns: "shiftScore"})}
                        </Tag>
                    );
                    break;
                default:
                    break;
            }
        }
        return tag;
    };

    const getRowClassName = item => {
        if (item.isLastshift) {
            return "lastShift";
        }
        return item.startTime && stores.meStore.checkRole("notVisibleForCompanyUser")
            ? (() => {
                  const diff = moment(item.startTime, "DD-MM-YYYY HH:mm:ss").diff(
                      moment(`${item.date}T${item.shiftStartTime}`, "DD-MM-YYYYTHH:mm"),
                      "minutes"
                  );
                  switch (true) {
                      case diff > 0 && diff < 5:
                          return "yellow";
                      case diff > 5 && diff < 15:
                          return "orange";
                      case diff > 15:
                          return "red";
                      default:
                          return null;
                  }
              })()
            : "gray";
    };

    const getWorkTime = item => {
        if (item.isActiveShift && item.startTime) {
            const diff = moment().diff(moment(item.startTime, "DD-MM-YYYY HH:mm:ss"), "minutes");
            return diff;
        } else if (item.workTimeMinute) {
            return item.workTimeMinute;
        } else {
            return 0;
        }
    };

    const renderLateStartIcon = lateStart => {
        return stores.meStore.checkRole("notVisibleForCompanyUser") && lateStart > 0 ? (
            <Tooltip
                title={
                    lateStart
                        ? `${lateStart}${t("units.minute.short", {
                              ns: "common"
                          })} ${t("shiftScore.lateShiftStart", {
                              ns: "shiftScore"
                          }).toLocaleLowerCase()}`
                        : 0
                }
            >
                <LateStartIcon style={{marginLeft: "5px"}} />
            </Tooltip>
        ) : (
            ""
        );
    };

    const renderEarlyEndIcon = earlyEnd => {
        return stores.meStore.checkRole("notVisibleForCompanyUser") && earlyEnd > 0 ? (
            <Tooltip
                title={
                    earlyEnd
                        ? `${earlyEnd}${t("units.minute.short", {
                              ns: "common"
                          })} ${t("shiftScore.earlyShiftEnd", {
                              ns: "shiftScore"
                          }).toLocaleLowerCase()}`
                        : 0
                }
            >
                <EarlyEndIcon />
            </Tooltip>
        ) : (
            ""
        );
    };

    // API bu ekranda planlanan vardiyayı tam tarih-saat olarak döndürüyor; tabloda sadece saat gösteriyoruz
    const formatScheduledTime = value => {
        if (!value) return "-";
        const parsed = moment(
            value,
            ["YYYY-MM-DD HH:mm:ss", "DD.MM.YYYY HH:mm:ss", "HH:mm:ss", "HH:mm"],
            true
        );
        return parsed.isValid() ? parsed.format("HH:mm") : value;
    };

    const renderActualShiftCell = row => {
        const {startTime, endTime} = row;
        if (!startTime && !endTime) {
            return <span>-</span>;
        }

        return (
            <>
                <span data-testid="startTime-hours">
                    {row.startTime
                        ? moment(row.startTime, "DD.MM.YYYY HH:mm").format("HH:mm")
                        : "-"}
                    {row?.lateStart && renderLateStartIcon(row?.lateStart)}
                </span>
                {row.isActiveShift ? (
                    <span style={{marginLeft: "5px"}}>
                        - <b style={{color: "gray"}}> ....</b>
                    </span>
                ) : (
                    <>
                        <EndTimeSpan data-testid="endTime-hours">
                            {row.endTime
                                ? `- ${moment(row.endTime, "DD.MM.YYYY HH:mm").format("HH:mm")}`
                                : "-"}
                             
                        </EndTimeSpan>
                    </>
                )}
                {row?.earlyEnd > 0 && renderEarlyEndIcon(row?.earlyEnd)}
            </>
        );
    };

    return (
        <>
            {actionModals}
            <ResponsiveDataTable
                size="middle"
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    pageSize: 10 + (data?.some(d => d.isLastshift) ? 1 : 0)
                }}
                rowKey="_id"
                dataSource={data}
                data-testid="shiftTable"
                loading={loading}
                display={data.length > 0 ? "grid" : "flex"}
                rowClassName={item => getRowClassName(item)}
            >
                <TableWrapper.Column
                    dataIndex="date"
                    title={t("fields.date", {ns: "common"})}
                    render={date => (
                        <span data-testid="reportDate">
                            {!date ? "-" : moment(date, "DD-MM-YYYY").format("DD.MM.YYYY")}
                        </span>
                    )}
                    onCell={() => ({
                        "data-label": t("fields.date", {ns: "common"})
                    })}
                />
                <TableWrapper.Column
                    dataIndex="fullname"
                    title={t("fields.courier", {ns: "common"})}
                    render={(fullname, data) => (
                        <span data-testid="fullname">
                            {!isPastShifts && (
                                <span
                                    style={{
                                        display: "inline-block",
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor: data?.isCourierCurrentlyActive
                                            ? "#aef001"
                                            : "#cf1322",
                                        marginRight: "8px",
                                        verticalAlign: "middle"
                                    }}
                                />
                            )}
                            {stores.meStore.checkRole("courierDetail") ? (
                                <CourierLink
                                    target="_blank"
                                    to={`/couriers/${data?.courierId ||
                                        data?.beforeLog?.courierId}`}
                                >
                                    {fullname}
                                </CourierLink>
                            ) : (
                                fullname
                            )}
                        </span>
                    )}
                    onCell={() => ({
                        "data-label": t("fields.courier", {ns: "common"})
                    })}
                />

                {(stores.meStore.checkRole("userPermission") ||
                    stores.meStore.auth?.companies?.length !== 1) && (
                    <TableWrapper.Column
                        dataIndex="companyName"
                        title={t("fields.company", {ns: "common"})}
                        render={(companyName, row) => (
                            <>
                                <span data-testid="companyName">{companyName}</span>
                            </>
                        )}
                        onCell={() => ({
                            "data-label": t("fields.company", {ns: "common"})
                        })}
                    />
                )}

                {(stores.meStore.checkRole("userPermission") ||
                    stores.meStore.auth?.subCompany === undefined ||
                    stores.meStore.auth?.subcompanies?.length > 1) && (
                    <TableWrapper.Column
                        dataIndex="subCompanyName"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={(subCompanyName, row) => (
                            <>
                                <span data-testid="subCompanyNmae">{subCompanyName}</span>
                            </>
                        )}
                        onCell={() => ({
                            "data-label": t("fields.subcompany", {ns: "common"})
                        })}
                    />
                )}
                <TableWrapper.Column
                    dataIndex="shiftStartTime"
                    title={t("shiftScore.scheduledShift", {ns: "shiftScore"})}
                    render={(shiftStartTime, row) => (
                        <span data-testid="date">
                            {`${formatScheduledTime(row?.shiftStartTime)} - ${formatScheduledTime(
                                row?.shiftEndTime
                            )}`}
                        </span>
                    )}
                    onCell={() => ({
                        "data-label": t("shiftScore.scheduledShift", {ns: "shiftScore"})
                    })}
                />
                <TableWrapper.Column
                    dataIndex="startTime"
                    title={t("shiftScore.actualShift", {ns: "shiftScore"})}
                    render={(startTime, row) => <span>{renderActualShiftCell(row)}</span>}
                    onCell={() => ({
                        "data-label": t("shiftScore.actualShift", {ns: "shiftScore"})
                    })}
                />

                <TableWrapper.Column
                    dataIndex="workTimeMinute"
                    title={t("shiftScore.workTime", {
                        ns: "shiftScore"
                    })}
                    render={(workTimeMinute, item) => (
                        <>
                            <span data-testid="workTimeMinute">
                                {minuteToString(getWorkTime(item))}
                            </span>
                        </>
                    )}
                    onCell={() => ({
                        "data-label": t("shiftScore.workTime", {ns: "shiftScore"})
                    })}
                />

                <TableWrapper.Column
                    dataIndex="packageCount"
                    title={t("shiftScore.packageCount", {ns: "shiftScore"})}
                    render={(packageCount, item) => (
                        <>
                            <span data-testid="packageCount">
                                {packageCount || 0}
                                {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                    item?.reason && (
                                        <Tooltip title={item?.reason}>
                                            {" "}
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    )}
                            </span>
                        </>
                    )}
                    onCell={() => ({
                        "data-label": t("shiftScore.packageCount", {ns: "shiftScore"})
                    })}
                />
                {!hideVerifiedPackageCount && (
                    <TableWrapper.Column
                        dataIndex="verifiedPackageCount"
                        title={t("shiftScore.verifiedPackageCount", {ns: "shiftScore"})}
                        render={(verifiedPackageCount, item) => (
                            <>
                                <span data-testid="verifiedPackageCount">
                                    {verifiedPackageCount || 0}
                                    {stores.meStore.checkRole("notVisibleForCompanyUser") &&
                                        item?.reason && (
                                            <Tooltip title={item?.reason}>
                                                {" "}
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        )}
                                </span>
                            </>
                        )}
                        onCell={() => ({
                            "data-label": t("shiftScore.verifiedPackageCount", {ns: "shiftScore"})
                        })}
                    />
                )}

                {!hideActionsColumn &&
                    !isDedicatedIntegration &&
                    !stores.meStore.checkRole("hideForOperationRole") && (
                        <TableWrapper.Column
                            title={t("actions.action", {ns: "common"})}
                            dataIndex="_id"
                            render={(_id, item) => <span>{renderActionButtons(_id, item)} </span>}
                            onCell={() => ({
                                "data-label": t("actions.action", {ns: "common"})
                            })}
                        />
                    )}
                {!hideStatusColumn && (
                    <TableWrapper.Column
                        title={t("fields.status", {ns: "common"})}
                        dataIndex="status"
                        render={(status, item) => <span>{renderStatus(item, status)}</span>}
                        onCell={() => ({
                            "data-label": t("fields.status", {ns: "common"})
                        })}
                    />
                )}
            </ResponsiveDataTable>
        </>
    );
};

export default ShiftScoreTable;
